import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ImportStepIndicator } from "../../components/dealer/InventoryImportShared";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import {
  downloadInventoryImportTemplate,
  trackImportEvent,
  uploadDealerImport,
} from "../../services/automapInventoryImportApi";

export default function DealerInventoryImportPage() {
  const { dealerId } = useOutletContext();
  const navigate = useNavigate();
  const { t, translateError, importModeOptions, publishModeOptions, templateFieldDefs } =
    useDealerPortalIntl();
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState("create_and_update");
  const [publishMode, setPublishMode] = useState("needs_review");
  const [confirmed, setConfirmed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    trackImportEvent("inventory_import_page_viewed", { dealerId });
  }, [dealerId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      setError(t("import.selectFile"));
      return;
    }
    if (!confirmed) {
      setError(t("import.confirmRequired"));
      return;
    }

    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("importMode", importMode);
      formData.append("publishMode", publishMode);
      formData.append("inventoryConfirmed", "true");
      const result = await uploadDealerImport(dealerId, formData);
      trackImportEvent("inventory_file_uploaded", { importId: result.import?.id, dealerId });
      navigate(`/v/automap/dealer/inventario/importar/${result.import.id}`);
    } catch (err) {
      setError(translateError(err.message, "loadImport"));
      trackImportEvent("inventory_import_failed", { dealerId, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="import-page">
      <ImportStepIndicator currentStep="upload" />
      <div className="import-page-header">
        <div>
          <h2>{t("import.title")}</h2>
          <p className="muted">{t("import.subtitle")}</p>
        </div>
        <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario/importaciones">
          {t("import.viewHistory")}
        </Link>
      </div>

      <div className="import-instructions dealer-state-box">
        <h3>{t("import.instructionsTitle")}</h3>
        <ul>
          <li>{t("import.instructionTemplate")}</li>
          <li>{t("import.instructionNormalize")}</li>
          <li>{t("import.instructionPhotos")}</li>
          <li>{t("import.instructionFinancing")}</li>
          <li>{t("import.instructionMaxRows")}</li>
        </ul>
        <button
          type="button"
          className="dealer-portal-btn"
          onClick={() => {
            downloadInventoryImportTemplate("dealer");
            trackImportEvent("inventory_template_downloaded", { dealerId });
          }}
        >
          {t("import.downloadTemplate")}
        </button>
      </div>

      <form className="import-upload-form dealer-state-box" onSubmit={handleSubmit}>
        <label>
          {t("import.csvFile")}
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>

        <label>
          {t("import.importMode")}
          <select value={importMode} onChange={(e) => setImportMode(e.target.value)}>
            {importModeOptions.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t("import.publishMode")}
          <select value={publishMode} onChange={(e) => setPublishMode(e.target.value)}>
            {publishModeOptions.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
        </label>

        <label className="import-checkbox">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t("import.confirmCheckbox")}
        </label>

        {error ? <p className="import-error">{error}</p> : null}

        <div className="import-actions">
          <button type="submit" className="dealer-portal-btn primary" disabled={uploading}>
            {uploading ? t("import.processing") : t("import.continue")}
          </button>
          <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario">
            {t("common.cancel")}
          </Link>
        </div>
      </form>

      <details className="dealer-state-box">
        <summary>{t("import.expectedColumns", { count: templateFieldDefs.length })}</summary>
        <p className="muted">{templateFieldDefs.map((field) => field.key).join(", ")}</p>
      </details>
    </div>
  );
}
